import { apiClient } from '@app/api';
import { toTimestamp } from '@mi-front/core';
import { DurationForRange, StreamEl } from '@mi-front/ui';
import { Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { useRef } from 'react';
import { useActiveStation } from '../../../../../../../core/stations/hooks/useActiveStation';
import { useStreamSegmentsRange } from '../../../hooks/segments/useStreamSegmentsQuery';
import { ScreenbugEnum, ScreenbugResponseType } from './types';

const getScreenbugEnum = (screenbug: string) => {
  switch (screenbug) {
    case 'ScreenBugLabel.SCREEN_BUG_COLORED':
      return ScreenbugEnum['ScreenBugLabel.SCREEN_BUG_COLORED'];
    case 'ScreenBugLabel.BACKGROUND':
      return ScreenbugEnum['ScreenBugLabel.BACKGROUND'];
    default:
      return ScreenbugEnum['ScreenBugLabel.SCREEN_BUG_GRAYSCALE_TRANSPARENT'];
  }
};

const getColor = (screenbug: ScreenbugResponseType) => {
  switch (screenbug.screen_bug) {
    case 'ScreenBugLabel.SCREEN_BUG_COLORED':
      return '#FFB199';
    case 'ScreenBugLabel.BACKGROUND':
      return '#A7D8F0';
    default:
      return '#D3D3D3';
  }
};

export function ScreenbugFocus() {
  const range = useStreamSegmentsRange();
  const { slug } = useActiveStation();
  const targetRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['screenbug', range.from, range.to],
    queryFn: () => apiClient.audit.fetchScreenbugRange(slug, range).then((res) => res),
  });

  if (!isLoading && !data?.length) {
    return null;
  }

  return (
    <div ref={targetRef}>
      {data?.map((screenbug: ScreenbugResponseType, index: number) => {
        const color = getColor(screenbug);
        return (
          <StreamEl
            key={screenbug.from}
            ts={toTimestamp(screenbug.from)}
            position="bottom"
            style={{
              pointerEvents: 'auto',
            }}
          >
            {(scale) => {
              const width = scale(toTimestamp(screenbug.to))! - scale(toTimestamp(screenbug.from))!;
              return (
                <Tooltip title={<ScreenbugContent screenbug={screenbug} />}>
                  <div
                    style={{
                      fontSize: '11px',
                      borderRadius: 4,
                      backgroundColor: color,
                      background: color,
                      width,
                    }}
                  >
                    {width >= 175 ? (
                      <ScreenbugContent screenbug={screenbug} />
                    ) : (
                      <div
                        style={{
                          position: 'relative',
                          cursor: 'pointer',
                          width: '100%',
                        }}
                      >
                        {/*U+200E is an invisible character, it is needed to render an StreamEl without text */}
                        ‎
                      </div>
                    )}
                  </div>
                </Tooltip>
              );
            }}
          </StreamEl>
        );
      })}
    </div>
  );
}

export function ScreenbugLabel({ screenbug }: { screenbug: ScreenbugResponseType }) {
  return <span>{getScreenbugEnum(screenbug.screen_bug)}</span>;
}

export function ScreenbugContent({ screenbug }: { screenbug: ScreenbugResponseType }) {
  return (
    <div
      style={{
        position: 'relative',
        cursor: 'pointer',
        width: '100%',
      }}
    >
      <small className="font-weight-bold">
        <ScreenbugLabel screenbug={screenbug} />
      </small>
      {' - '}
      <small>
        {moment(screenbug.from).format('HH:mm:ss')} (
        <DurationForRange from={screenbug.from} to={screenbug.to} />)
      </small>
    </div>
  );
}
